import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { showingService } from '../../_services/showing.service';
import { userService } from '../../_services/user.service';

function useCurrentUser() {
  const [user, setUser] = useState(userService.currentUserValue);
  useEffect(() => {
    const sub = userService.currentUser.subscribe(setUser);
    return () => sub.unsubscribe();
  }, []);
  return user;
}

function fmtWindow(s) {
  const a = new Date(s.window_start), b = new Date(s.window_end);
  const day = a.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const t = d => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${t(a)}–${t(b)}`;
}

function coordsOf(job) {
  const p = job.properties && job.properties[0];
  if (p && p.location && p.location.coordinates && p.location.coordinates.length === 2) {
    return { lat: p.location.coordinates[1], lng: p.location.coordinates[0] };
  }
  return null;
}

function milesBetween(a, b) {
  const rad = x => (x * Math.PI) / 180;
  const R = 3958.8;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(h));
}

// how soon does the window open?
function urgency(s) {
  const ms = new Date(s.window_start) - new Date();
  if (ms < 0) return { flag: 'soon', label: 'Window open now' };
  const hrs = ms / 36e5;
  if (hrs <= 3) return { flag: 'soon', label: `Starts in ${Math.max(1, Math.round(hrs))} h` };
  if (new Date(s.window_start).toDateString() === new Date().toDateString()) return { flag: 'today', label: 'Today' };
  return null;
}

export default function JobsFeed() {
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState(null);
  const [acceptedId, setAcceptedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const meRef = useRef(null); // my coords, once geolocation resolves
  const user = useCurrentUser();
  const isMine = j => user && j.buyer_agent && String(j.buyer_agent.user_id) === String(user._id);

  const load = () => {
    const run = coords => showingService.jobs(coords).then(res => {
      if (res.status === 'success') setJobs(res.showings);
      else setError(res.message);
    }).catch(() => setError('Could not load jobs.'));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          meRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          run({ lng: pos.coords.longitude, lat: pos.coords.latitude, radius: 50 });
        },
        () => run(null),
        { timeout: 4000 }
      );
    } else run(null);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // keep the feed fresh
    return () => clearInterval(t);
  }, []);

  const accept = id => {
    setBusyId(id);
    setError(null);
    showingService.accept(id).then(res => {
      setBusyId(null);
      if (res.status === 'success') {
        setAcceptedId(id);
        load();
      } else if (res.status === 'license_not_verified') {
        setError(<>Your license needs to be verified before you can accept showings. <Link to="/users/profile/">Head to your profile</Link> to submit it.</>);
      } else if (res.status === 'profile_incomplete') {
        setError(<>Complete your profile before accepting showings. <Link to="/complete-profile">Go to your profile</Link>.</>);
      } else if (res.status === 'already_taken') {
        setError('Someone beat you to that one — it just got taken.');
        load();
      } else {
        setError(res.message || 'Could not accept.');
      }
    }).catch(() => { setBusyId(null); setError('Network error — try again.'); });
  };

  const avgFee = jobs && jobs.length ? Math.round(jobs.reduce((n, j) => n + j.fee, 0) / jobs.length) : 0;
  const todayCount = jobs ? jobs.filter(j => new Date(j.window_start).toDateString() === new Date().toDateString()).length : 0;

  return (
    <div className="sh-page sh-page-wide">
      <div className="mp-head">
        <div>
          <h1 style={{ margin: 0 }}>Open showings near you</h1>
          <p className="sh-page-sub" style={{ margin: '4px 0 0' }}>Fee and time are up front. Accept a job and it's yours — the exact address and access notes unlock once you accept.</p>
        </div>
        <Link className="sh-btn sh-btn-outline" to="/map">Map view</Link>
      </div>

      {error ? <div className="sh-error" style={{ marginBottom: 18 }}>{error}</div> : null}
      {acceptedId ? <div className="sh-success" style={{ marginBottom: 18 }}>Showing accepted — it's in <Link to="/showings/appointments">Showing appointments</Link>. The requesting agent has your contact info.</div> : null}

      {jobs && jobs.length > 0 ? (
        <div className="tk-summary">
          <div className="tk-stat"><div className="v">{jobs.length}</div><div className="k">Open showing{jobs.length === 1 ? '' : 's'}</div></div>
          <div className="tk-stat"><div className="v">${avgFee}</div><div className="k">Average fee</div></div>
          <div className="tk-stat"><div className="v">{todayCount}</div><div className="k">Happening today</div></div>
        </div>
      ) : null}

      {jobs === null ? (
        <div className="wd-empty"><b>Loading nearby showings…</b></div>
      ) : jobs.length === 0 ? (
        <div className="wd-empty">
          <div className="glyph">🔭</div>
          <b>No open showings right now</b>
          <p>New jobs are posted throughout the day — check back soon or keep the tab open, the feed refreshes itself.</p>
        </div>
      ) : (
        <div className="tk-list">
          {jobs.map(j => {
            const p = j.properties[0] || {};
            const urg = urgency(j);
            const ll = coordsOf(j);
            const dist = ll && meRef.current ? milesBetween(meRef.current, ll) : null;
            const perHour = j.duration_minutes ? Math.round((j.fee / j.duration_minutes) * 60) : null;
            return (
              <div className="tk-card" key={j.request_id}>
                <div className="tk-main">
                  <div className="tk-title-row">
                    <span className="tk-title">
                      {[p.city, p.state].filter(Boolean).join(', ') || 'Showing'}
                      {j.properties.length > 1 ? ` · ${j.properties.length} stops` : ''}
                    </span>
                    {urg ? <span className={'tk-flag ' + urg.flag}>{urg.label}</span> : null}
                  </div>
                  <div className="tk-chips">
                    <span className="tk-chip"><span className="ic">📅</span>{fmtWindow(j)}</span>
                    <span className="tk-chip"><span className="ic">⏱</span>{j.duration_minutes} min</span>
                    {p.zip ? <span className="tk-chip"><span className="ic">📍</span>{p.zip}</span> : null}
                    {dist != null ? <span className="tk-chip"><span className="ic">🚗</span>{dist < 1 ? '<1' : Math.round(dist)} mi away</span> : null}
                  </div>
                  {j.notes ? <div className="tk-notes">{j.notes}</div> : null}
                  {j.buyer_agent && j.buyer_agent.name ? <div className="tk-by">Posted by {j.buyer_agent.name}</div> : null}
                </div>
                <div className="tk-stub">
                  <span className="tk-fee">${j.fee}</span>
                  {perHour ? <span className="tk-rate">≈ ${perHour}/hr</span> : null}
                  {isMine(j) ? (
                    <span className="sh-status open">Your posting</span>
                  ) : (
                    <button
                      className="sh-btn sh-btn-green"
                      disabled={busyId === j.request_id}
                      onClick={() => accept(j.request_id)}
                    >
                      {busyId === j.request_id ? 'Accepting…' : 'Accept showing'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
